const TRANSITION = 'transition'
const ANIMATION = 'animation'

const transitionEndEvent = 'transitionend'
const animationEndEvent = 'animationend'

const animationFrameLength = Math.ceil(1000 / 60)

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export function whenTransitionEnds(el: Element, cb: () => void): () => void {
  const { type, timeout, propCount } = getTransitionInfo(el)
  if (!type) {
    cb()
    return noop
  }

  const eventType = type === TRANSITION ? transitionEndEvent : animationEndEvent
  let ended = 0
  let timeoutId: number | null = null
  const cleanup = () => {
    if (timeoutId) {
      window.clearTimeout(timeoutId)
      timeoutId = null
    }
    el.removeEventListener(eventType, onEnd)
  }
  const end = () => {
    cleanup()
    cb()
  }
  const onEnd = (e: Event) => {
    if (e.target === el) {
      if (++ended >= propCount) {
        end()
      }
    }
  }

  timeoutId = window.setTimeout(() => {
    if (ended < propCount) {
      end()
    }
  }, timeout + animationFrameLength)
  el.addEventListener(eventType, onEnd)

  return () => cleanup()
}

export function getTransitionInfo(el: Element): {
  type: typeof TRANSITION | typeof ANIMATION | null
  propCount: number
  timeout: number
} {
  const styles = window.getComputedStyle(el)
  // JSDOM may return undefined for transition properties
  const transitionDelays = (styles.transitionDelay || '').split(', ')
  const transitionDurations = (styles.transitionDuration || '').split(', ')
  const transitionTimeout = getTimeout(transitionDelays, transitionDurations)
  const animationDelays = (styles.animationDelay || '').split(', ')
  const animationDurations = (styles.animationDuration || '').split(', ')
  const animationTimeout = getTimeout(animationDelays, animationDurations)

  const timeout = Math.max(transitionTimeout, animationTimeout)
  const type = timeout > 0 ? (transitionTimeout > animationTimeout ? TRANSITION : ANIMATION) : null
  const propCount = type ? (type === TRANSITION ? transitionDurations.length : animationDurations.length) : 0

  return { type, timeout, propCount }
}

function getTimeout(delays: string[], durations: string[]): number {
  while (delays.length < durations.length) {
    delays = delays.concat(delays)
  }

  return Math.max.apply(
    null,
    durations.map((d, i) => {
      return toMs(d) + toMs(delays[i])
    }),
  )
}

// Old versions of Chromium (below 61.0.3163.100) formats floating pointer numbers
// in a locale-dependent way, using a comma instead of a dot.
// If comma is not replaced with a dot, the input will be rounded down (i.e. acting
// as a floor function) causing unexpected behaviors
function toMs(s: string): number {
  return Number(s.slice(0, -1).replace(',', '.')) * 1000
}
